import { createFileRoute, Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
  HiCheck,
  HiOutlineAcademicCap,
  HiOutlineBookOpen,
  HiOutlineClock,
} from 'react-icons/hi'
import { HiOutlineTrophy } from 'react-icons/hi2'
import { useAllInCodeProgress } from '@/hooks/useInCodeProgress'
import { landscapeOfDeceptionChapter } from '@/learning-paths/incode/landscape-of-deception/chapter'
import { emotionalHijackChapter } from '@/learning-paths/incode/emotional-hijack/chapter'
import { pauseAndVerifyChapter } from '@/learning-paths/incode/pause-and-verify/chapter'
import { warningSignsChapter } from '@/learning-paths/incode/warning-signs/chapter'
import { buildDefencesV2Chapter } from '@/learning-paths/incode/build-defences-v2/chapter'
import { whatToDoV2Chapter } from '@/learning-paths/incode/what-to-do-v2/chapter'
import emotionalHijackIcon from '@/assets/emotional-hijack.jpg'
import pauseAndVerifyIcon from '@/assets/pause-and-verify.jpg'
import warningSignsIcon from '@/assets/chapter3-warning.jpg'
import hardTargetIcon from '@/assets/chapter4-hardtarget.jpg'

export const Route = createFileRoute(
  '/_auth/learning-journeys/scam-fundamentals',
)({
  component: ScamFundamentalsPage,
})

const ease = [0.16, 1, 0.3, 1] as const

// Chapter list is sourced from each chapter's own definition (title,
// description, estimatedMinutes, slides) — single source of truth. Only the
// bits that are overview-specific (ordinal number, route, icon) are set here.
const chapters = [
  {
    number: 1,
    chapter: landscapeOfDeceptionChapter,
    route: '/learning-journeys/new-age-of-scams' as const,
    icon: null as string | null,
  },
  {
    number: 2,
    chapter: emotionalHijackChapter,
    route: '/learning-journeys/emotional-hijack' as const,
    icon: emotionalHijackIcon,
  },
  {
    number: 3,
    chapter: pauseAndVerifyChapter,
    route: '/learning-journeys/pause-and-verify' as const,
    icon: pauseAndVerifyIcon,
  },
  {
    number: 4,
    chapter: warningSignsChapter,
    route: '/learning-journeys/warning-signs' as const,
    icon: warningSignsIcon,
  },
  {
    number: 5,
    chapter: buildDefencesV2Chapter,
    route: '/learning-journeys/digital-self-defence' as const,
    icon: hardTargetIcon,
  },
  {
    number: 6,
    chapter: whatToDoV2Chapter,
    route: '/learning-journeys/what-to-do-after-a-scam' as const,
    icon: null as string | null,
  },
]

const totalMinutes = chapters.reduce(
  (sum, ch) => sum + ch.chapter.estimatedMinutes,
  0,
)

function ScamFundamentalsPage() {
  const { data: progressData } = useAllInCodeProgress()

  const getChapterProgress = (ch: typeof chapters[number]) => {
    const total = ch.chapter.slides.length
    if (!progressData || total === 0) return 0
    const completed = progressData[ch.chapter.id]?.length ?? 0
    return Math.min(100, Math.round((completed / total) * 100))
  }

  const overallCompleted = chapters.reduce((sum, ch) => {
    if (!progressData) return sum
    return sum + (progressData[ch.chapter.id]?.length ?? 0)
  }, 0)
  const overallTotal = chapters.reduce((sum, ch) => sum + ch.chapter.slides.length, 0)
  const overallPercent = overallTotal > 0 ? Math.min(100, Math.round((overallCompleted / overallTotal) * 100)) : 0

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-5xl px-6 pb-20 pt-8">
        {/* Breadcrumb */}
        <motion.nav
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, ease }}
          className="flex items-center gap-2 text-sm"
        >
          <Link
            to="/learn"
            search={{
              tab: undefined,
              tour: undefined,
              invitationcode: undefined,
              __clerk_ticket: undefined,
            }}
            className="text-violet-600 font-semibold underline underline-offset-2 hover:text-violet-700 transition-colors cursor-pointer"
          >
            Training
          </Link>
          <span className="text-slate-300">/</span>
          <span className="text-slate-900 font-medium">Scam Defense Fundamentals</span>
        </motion.nav>

        {/* Hero: two-column */}
        <div className="mt-8 flex flex-col lg:flex-row lg:gap-12">
          {/* Left column */}
          <div className="flex-1 min-w-0">
            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease }}
              className="text-3xl sm:text-4xl md:text-5xl font-semibold text-slate-900 tracking-tight leading-[1.08]"
            >
              Scam Defense Fundamentals
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease }}
              className="mt-5 text-base sm:text-lg text-slate-500 leading-relaxed max-w-xl"
            >
              Learn to identify the tactics behind today{'\u2019'}s most common scams
              and build a simple routine for staying safe. Through practical examples
              and exercises, you{'\u2019'}ll build confidence to protect yourself and those you care for.
            </motion.p>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35, ease }}
              className="mt-7"
            >
              <Link
                to="/learning-journeys/new-age-of-scams"
                className="inline-flex items-center gap-3 rounded-xl bg-violet-600 px-6 py-3.5 text-base font-semibold text-white hover:bg-violet-700 transition-colors cursor-pointer"
              >
                <span>Start Chapter 1: The New Age of Scams</span>
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </Link>
            </motion.div>
          </div>

          {/* Right column: Guide Overview */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease }}
            className="mt-10 lg:mt-0 lg:w-80 shrink-0"
          >
            {(() => {
              const completedChapters = chapters.filter(
                (ch) => getChapterProgress(ch) >= 100,
              ).length
              const status =
                completedChapters === chapters.length
                  ? 'Complete'
                  : overallPercent > 0
                    ? 'In progress'
                    : 'Not started'
              const statusDot =
                status === 'Complete'
                  ? 'bg-emerald-500'
                  : status === 'In progress'
                    ? 'bg-violet-500'
                    : 'bg-slate-300'

              return (
                <div className="rounded-2xl bg-white shadow-sm ring-1 ring-slate-200/80 px-5 py-5">
                  {/* Status anchors the top of the card on its own — no
                      generic "Overview" label needed. */}
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${statusDot}`} />
                    <span className="text-sm font-medium text-slate-700">{status}</span>
                  </div>

                  {/* Progress: per-chapter segmented bar */}
                  <div className="mt-5">
                    <div className="flex items-baseline justify-between">
                      <p className="text-xs text-slate-500">
                        <span className="font-semibold text-slate-900 tabular-nums">
                          {completedChapters}
                        </span>
                        <span className="text-slate-400"> of {chapters.length} chapters</span>
                      </p>
                      <p className="text-xs font-medium text-slate-600 tabular-nums">
                        {overallPercent}%
                      </p>
                    </div>
                    <div className="mt-2 flex gap-1">
                      {chapters.map((ch) => {
                        const p = getChapterProgress(ch)
                        return (
                          <div
                            key={ch.number}
                            className="flex-1 h-1.5 rounded-full bg-slate-200/70 overflow-hidden"
                          >
                            <div
                              className="h-full bg-violet-500 transition-all duration-500"
                              style={{ width: `${p}%` }}
                            />
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* Key stats */}
                  <div className="mt-6 grid grid-cols-2 gap-y-4 gap-x-4 text-sm">
                    <div>
                      <p className="text-xs text-slate-400">Total time</p>
                      <p className="mt-0.5 flex items-center gap-1.5 font-medium text-slate-900">
                        <HiOutlineClock className="h-4 w-4 text-slate-400" />
                        <span className="tabular-nums">~{totalMinutes} min</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Chapters</p>
                      <p className="mt-0.5 flex items-center gap-1.5 font-medium text-slate-900">
                        <HiOutlineBookOpen className="h-4 w-4 text-slate-400" />
                        <span className="tabular-nums">{chapters.length}</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Difficulty</p>
                      <p className="mt-0.5 flex items-center gap-1.5 font-medium text-slate-900">
                        <HiOutlineAcademicCap className="h-4 w-4 text-slate-400" />
                        <span>Beginner</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Points</p>
                      <p className="mt-0.5 flex items-center gap-1.5 font-medium text-slate-900">
                        <HiOutlineTrophy className="h-4 w-4 text-slate-400" />
                        <span className="tabular-nums">+50</span>
                      </p>
                    </div>
                  </div>

                  {/* Support */}
                  <div className="mt-5 border-t border-slate-200/70 pt-4">
                    <p className="text-sm text-slate-500 leading-relaxed">
                      Stuck on something?{' '}
                      <Link
                        to="/contact-support"
                        className="font-medium text-violet-600 hover:text-violet-700 cursor-pointer"
                      >
                        Contact support.
                      </Link>
                    </p>
                  </div>
                </div>
              )
            })()}
          </motion.div>
        </div>

        {/* Chapter list */}
        <div className="mt-14">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4, ease }}
          >
            <h2 className="flex items-center gap-2 text-xl sm:text-2xl font-semibold text-slate-900">
              <svg className="h-5 w-5 sm:h-6 sm:w-6 text-slate-900" fill="currentColor" viewBox="0 0 24 24">
                <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z" />
              </svg>
              Chapters
            </h2>
            <p className="mt-1 text-sm text-slate-400">Work through each chapter at your own pace.</p>
          </motion.div>

          <div className="relative mt-6">
            {/* Base line */}
            <div className="absolute left-[19px] top-6 bottom-6 w-[2px] bg-slate-200" />
            {/* Progress fill (height tied to overall course progress) */}
            <div
              className="absolute left-[19px] top-6 w-[2px] bg-violet-500 transition-all duration-700"
              style={{ height: `calc((100% - 3rem) * ${overallPercent / 100})` }}
            />

            <div className="space-y-6">
              {chapters.map((ch, i) => {
                const progress = getChapterProgress(ch)
                const isDone = progress >= 100
                // "Current" = the first non-done chapter.
                const firstIncompleteIndex = chapters.findIndex(
                  (c) => getChapterProgress(c) < 100,
                )
                const isCurrent = i === firstIncompleteIndex && !isDone
                return (
                  <motion.div
                    key={ch.number}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.5 + i * 0.07, ease }}
                    className="relative pl-14"
                  >
                    {/* Node */}
                    <div className="absolute left-0 top-1">
                      {isDone ? (
                        <div className="h-10 w-10 rounded-full bg-violet-600 flex items-center justify-center shadow-sm">
                          <HiCheck className="h-5 w-5 text-white" strokeWidth={3} />
                        </div>
                      ) : isCurrent ? (
                        <div className="relative h-10 w-10 rounded-full bg-white border-2 border-violet-500 flex items-center justify-center">
                          <span className="text-sm font-semibold text-violet-600 tabular-nums">
                            {ch.number}
                          </span>
                          <span className="absolute inset-0 rounded-full border-2 border-violet-400 animate-ping opacity-40" />
                        </div>
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center">
                          <span className="text-sm font-semibold text-slate-400 tabular-nums">
                            {ch.number}
                          </span>
                        </div>
                      )}
                    </div>

                    <Link
                      to={ch.route}
                      className="group block rounded-xl -mx-3 px-3 py-2 hover:bg-slate-50/60 transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <h3
                          className={`text-xl sm:text-2xl font-semibold tracking-tight leading-tight transition-colors ${
                            isDone
                              ? 'text-slate-500 group-hover:text-slate-700'
                              : 'text-slate-900 group-hover:text-violet-700'
                          }`}
                        >
                          {ch.chapter.title}
                        </h3>
                        <span className="shrink-0 text-sm text-slate-400 tabular-nums pt-1.5">
                          {ch.chapter.estimatedMinutes} min
                        </span>
                      </div>
                      <p className="mt-2 text-base text-slate-500 leading-relaxed max-w-2xl line-clamp-2">
                        {ch.chapter.description}
                      </p>
                      {isCurrent && progress > 0 && (
                        <div className="mt-4 flex items-center gap-3 max-w-sm">
                          <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full bg-violet-500 transition-all duration-500"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <span className="text-sm text-slate-500 font-medium tabular-nums">
                            {progress}%
                          </span>
                        </div>
                      )}
                    </Link>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
